import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ScheduleExceptionCreate,
  ScheduleExceptionItem,
  UserDirectoryItem,
  WeeklySchedulePut,
  WeeklyScheduleResponse,
} from '@crm/shared';
import { api } from '../../lib/api';

export const schedulesKeys = {
  directory: ['users', 'directory'] as const,
  schedule: (userId: number) => ['schedules', userId] as const,
  exceptions: (userId: number | null) => ['schedule-exceptions', userId] as const,
};

export function useUserDirectory() {
  return useQuery({
    queryKey: schedulesKeys.directory,
    queryFn: async () => {
      const res = await api.get<UserDirectoryItem[]>('/users/directory');
      return res.data;
    },
  });
}

export function useUserSchedule(userId: number | null) {
  return useQuery({
    queryKey: userId ? schedulesKeys.schedule(userId) : ['schedules', 'noop'],
    enabled: userId !== null,
    queryFn: async () => {
      const res = await api.get<WeeklyScheduleResponse>(`/schedules/${userId}`);
      return res.data;
    },
  });
}

export function useUserScheduleSave(userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: WeeklySchedulePut) => {
      const res = await api.put<WeeklyScheduleResponse>(`/schedules/${userId}`, input);
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(schedulesKeys.schedule(userId), data);
    },
  });
}

export function useScheduleExceptions(userId: number | null) {
  return useQuery({
    queryKey: schedulesKeys.exceptions(userId),
    enabled: userId !== null,
    queryFn: async () => {
      const res = await api.get<ScheduleExceptionItem[]>('/schedule-exceptions', {
        params: { userId },
      });
      return res.data;
    },
  });
}

export function useScheduleExceptionCreate(userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ScheduleExceptionCreate) => {
      const res = await api.post<ScheduleExceptionItem>('/schedule-exceptions', input);
      return res.data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: schedulesKeys.exceptions(userId) }),
  });
}

export function useScheduleExceptionDelete(userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/schedule-exceptions/${id}`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: schedulesKeys.exceptions(userId) }),
  });
}
